import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { sortMenuByLabel } from "../../../../Helpers/sortMenuByLabel";

// Import enums and route definitions
import { sidebarEmployeeOptionsEnum } from "../../../../enums/sidebarEmployeeOptionsEnum";
import routes from "../../../../Routes/routes";


import { permissionObj } from "../../../../Helpers/permission";
import { IconsSideBar } from "./IconsSideBar";
import {MENU_NAME_MODEL} from "../../../LoginOption/Model/Model";
import {USER_TYPE_NAME_MODEL} from "../../../../enums/userTypeEnum";
import {MODELS} from "../../../../Apis/Models";


/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 */

// Main component to manage the Employee sidebar menu options
const EmployeeTabsApp = ({ currentUrl, setChangeStyle, parts, permission, userType, user }) => {
    const { t } = useTranslation();
    // Determine the active module part based on the current URL path
    const modulePart = parts[3] ? parts[3] + (parts[4] ? '/' + parts[4] : '') : '';
    const isGuard = user?.data?.userType?.name === USER_TYPE_NAME_MODEL.GUARD;
    // Residents only manage their own apartment and their profile, the rest of the modules stay hidden
    const isResident = userType === USER_TYPE_NAME_MODEL.RESIDENT;
    // Set the sidebar style based on the current module path
    useEffect(() => {
        setChangeStyle(sidebarEmployeeOptionsEnum[modulePart]);
    }, [currentUrl]);

    // Define menu items with permissions and submenu items (if any)
    const menuItems = [
        {
            name: MENU_NAME_MODEL.COMPANY,
            url: routes.company.update,
            label: 'company_profile',
            onlyName: true,
            icon: IconsSideBar.icCompany,
            isHovering: 1,
            hasPermission: permission?.includes(permissionObj.WEB_COMPANY_READ),
        },
        {
            name: MENU_NAME_MODEL.ZONES,
            url: routes.zone.list,
            label: 'first_access',
            onlyName: true,
            icon: IconsSideBar.icFirstAccess,
            isHovering: 2,
            hasPermission: permission?.includes(permissionObj.WEB_ZONE_READ),
        },
        {
            name: MENU_NAME_MODEL.DEVICE,
            url: routes.devices.list,
            label: 'devices',
            onlyName: true,
            icon: IconsSideBar.icDevices,
            isHovering: 22,
            hasPermission: permission?.includes(permissionObj.WEB_DEVICE_READ),
        },
        {
            name: MENU_NAME_MODEL.ATTENDANCE,
            url: routes.attendance.main,
            label: 'attendance',
            onlyName: true,
            icon: IconsSideBar.icAttendance,
            isHovering: 8,
            hasPermission:
                permission?.includes(permissionObj.WEB_ATTENDANCE_READ) ||
                permission?.includes(permissionObj.WEB_ATTENDANCE_SHIFT_READ) ||
                permission?.includes(permissionObj.WEB_ATTENDANCE_SETTING_READ) ||
                permission?.includes(permissionObj.WEB_ATTENDANCE_DEPARTMENT_POLICIES_READ) ||
                permission?.includes(permissionObj.WEB_ATTENDANCE_MANAGE_GEOFENCES_READ) ||
                permission?.includes(permissionObj.WEB_USER_SPECIAL_PERMISSION_READ) ||
                permission?.includes(permissionObj.WEB_ATTENDANCE_SUBMISSION_PERIOD_REVIEW_READ) ||
                permission?.includes(permissionObj.WEB_ATTENDANCE_SUBMISSION_REQUEST_READ) ||
                permission?.includes(permissionObj.WEB_ATTENDANCE_SUBMISSION_HISTORY_READ) ||
                permission?.includes(permissionObj.WEB_PAYROLL_POLICY_READ) ||
                permission?.includes(permissionObj.WEB_PAYROLL_DEDUCTION_POLICY_READ) ||
                permission?.includes(permissionObj.WEB_PAYROLL_DEDUCTION_POLICY_USER_READ) ||
                permission?.includes(permissionObj.WEB_PAYROLL_ACTIVE_READ) ||
                permission?.includes(permissionObj.WEB_PAYROLL_HISTORY_READ)
        },
        {
            name: MENU_NAME_MODEL.DEPARTMENTS,
            url: routes.department.list,
            label: 'departments',
            onlyName: true,
            icon: IconsSideBar.icDepartments,
            isHovering: 11,
            hasPermission: permission?.includes(permissionObj.WEB_DEPARTMENT_READ),
        },
        {
            name: MENU_NAME_MODEL.EMPLOYEES,
            url: routes.employee.list,
            label: 'employees',
            onlyName: true,
            icon: IconsSideBar.icEmployees,
            isHovering: 4,
            hasPermission: permission?.includes(permissionObj.WEB_EMPLOYEE_READ),
        },
        {
            name: 'guards',
            url: routes.guards.list,
            label: 'guards',
            onlyName: true,
            icon: IconsSideBar.icGuardTitle,
            isHovering: 140,
            hasPermission: permission?.includes(permissionObj.WEB_GUARD_ROSTER_READ) || permission?.includes(permissionObj.WEB_GUARD_ZONE_ACCESS_READ),
        },
        {
            name: MENU_NAME_MODEL.VEHICLES,
            url: routes.vehicleFleet.list,
            label: 'vehicle_fleet',
            onlyName: true,
            icon: IconsSideBar.icVehicleFleet,
            isHovering: 5,
            hasPermission: permission?.includes(permissionObj.WEB_VEHICLE_READ),
        },
        {
            name: MENU_NAME_MODEL.RESERVATIONS,
            url: routes.event.list,
            label: 'reservations',
            onlyName: true,
            icon: IconsSideBar.icEvents,
            isHovering: 3,
            hasPermission:
                permission?.includes(permissionObj.WEB_EVENT_READ) ||
                permission?.includes(permissionObj.WEB_EVENT_STATUS_READ) ||
                permission?.includes(permissionObj.WEB_EVENT_MANAGE_READ),
        },
        {
            name: MENU_NAME_MODEL.CONTROLLER,
            url: null,
            label: 'controller',
            onlyName: false,
            icon: IconsSideBar.icController,
            isHovering: 23,
            hasPermission: permission?.includes(permissionObj.WEB_HID_READ),
            itemsSubMenu: [
                { name: MENU_NAME_MODEL.CONTROLLER, url: routes.controller.list, icon: <IconsSideBar.icControllerSubmenu/> },
                { name: MENU_NAME_MODEL.SIOS, url: routes.controller.sios, icon: <IconsSideBar.icSios/> },
                { name: MENU_NAME_MODEL.READERS, url: routes.controller.readers, icon: <IconsSideBar.icReaders/> },
                { name: MENU_NAME_MODEL.INPUTS, url: routes.controller.inputs, icon: <IconsSideBar.icInput/> },
                { name: MENU_NAME_MODEL.OUTPUTS, url: routes.controller.outputs, icon: <IconsSideBar.icOutput/> },
                { name: MENU_NAME_MODEL.ACRS, url: routes.controller.acrs, icon: <IconsSideBar.icAcr/> },
            ],
        },
        {
            name: MENU_NAME_MODEL.WORK_SHIFT_PANEL,
            url: routes.workshift.list,
            label: 'work_shift_panel',
            onlyName: true,
            icon: IconsSideBar.icWorkshipPanel,
            isHovering: 13,
            hasPermission: permission?.includes(permissionObj.WEB_WORK_SHIFT_READ),
        },
        {
            name: MENU_NAME_MODEL.ACCESS,
            url: routes.access.list,
            label: 'access',
            onlyName: true,
            icon: IconsSideBar.icAccess,
            isHovering: 14,
            hasPermission:
                permission?.includes(permissionObj.WEB_ACCESS_USER_READ) ||
                permission?.includes(permissionObj.WEB_ACCESS_VEHICLE_READ),
        },
        {
            name: MENU_NAME_MODEL.PRIVILEGE_SETTINGS,
            url: routes.roles.list,
            label: 'privilage_settings',
            onlyName: true,
            icon: IconsSideBar.icPrivilageSettings,
            isHovering: 15,
            hasPermission: permission?.includes(permissionObj.WEB_PRIVILEGE_READ),
        },
        {
            name: MENU_NAME_MODEL.ONBOARDING,
            url: routes.onboarding.list,
            label: 'on_boarding',
            onlyName: true,
            icon: IconsSideBar.icOnBoarding,
            isHovering: 16,
            hasPermission: permission?.includes(permissionObj.WEB_ONBOARDING_READ),
        },
        {
            name: MENU_NAME_MODEL.CONTRACTORS,
            url: routes.contractor.list,
            label: 'contractors',
            onlyName: true,
            icon: IconsSideBar.icContractors,
            isHovering: 6,
            hasPermission:
                permission?.includes(permissionObj.WEB_CONTRACTOR_READ) ||
                permission?.includes(permissionObj.WEB_CONTRACTOR_CONTRACT_READ) ||
                permission?.includes(permissionObj.WEB_CONTRACTOR_REQUEST_READ) ||
                permission?.includes(permissionObj.WEB_CONTRACTOR_APPROVAL_FLOW_READ) ||
                permission?.includes(permissionObj.WEB_CONTRACTOR_MANAGE_READ),
        },
        {
            name: MENU_NAME_MODEL.SUPPLIER,
            url: routes.suppliers.main,
            label: 'supplier',
            onlyName: true,
            icon: IconsSideBar.icSupplier,
            isHovering: 7,
            hasPermission:
                permission?.includes(permissionObj.WEB_SUPPLIER_READ) ||
                permission?.includes(permissionObj.WEB_SUPPLIER_ORDER_READ) ||
                permission?.includes(permissionObj.WEB_SUPPLIER_APPROVAL_FLOW_READ) ||
                permission?.includes(permissionObj.WEB_SUPPLIER_REQUEST_READ) ||
                permission?.includes(permissionObj.WEB_SUPPLIER_MANAGE_READ),
        },
        {
            name: MENU_NAME_MODEL.DOCUMENT_PANEL,
            url: routes.document.list,
            label: 'user_document_management',
            onlyName: true,
            icon: IconsSideBar.icDocumentPanel,
            isHovering: 17,
            hasPermission: permission?.includes(permissionObj.WEB_EMPLOYEE_DOCUMENT_READ)
},
        {
            name: MENU_NAME_MODEL.EMAIL_SETTINGS,
            url: routes.email.list,
            label: 'email_service_settings',
            onlyName: true,
            icon: IconsSideBar.icEmailServiceSettings,
            isHovering: 19,
            hasPermission: permission?.includes(permissionObj.WEB_EMAIL_SETTINGS_READ),
        },
        {
            name: MENU_NAME_MODEL.BACKUP,
            url: routes.backup.list,
            label: 'back_up',
            onlyName: true,
            icon: IconsSideBar.icBackUp,
            isHovering: 10,
            hasPermission: permission?.includes(permissionObj.WEB_BACK_UP_READ),
        },
        {
            name: MENU_NAME_MODEL.WORK_OFFICE,
            url: routes.homeOffice.list,
            label: 'home_office',
            onlyName: true,
            icon: IconsSideBar.icHomeOffice,
            isHovering: 24,
            hasPermission: permission?.includes(permissionObj.WEB_HOME_OFFICE_READ),
        },
        {
            name: MENU_NAME_MODEL.CARD_DESIGNER,
            url: routes.cardDesigner.list,
            label: 'card_designer',
            onlyName: true,
            icon: IconsSideBar.icCardDesigner,
            isHovering: 25,
            hasPermission:
                permission?.includes(permissionObj.WEB_CARD_DESIGNER_READ) ||
                permission?.includes(permissionObj.WEB_RESOURCES_READ),
        },
        {
            name: MENU_NAME_MODEL.LOGS,
            url: routes.logPlatform.list,
            label: 'logs',
            onlyName: true,
            icon: IconsSideBar.icLogs,
            isHovering: 26,
            hasPermission: permission?.includes(permissionObj.WEB_LOG_PLATFORM_READ),
        },
        {
            name: MENU_NAME_MODEL.DELETE,
            url: routes.hardDelete.list,
            label: 'delete_and_restore',
            icon: IconsSideBar.icDeleteRestore,
            onlyName: true,
            isHovering: 32,
            hasPermission: permission?.includes(permissionObj.WEB_HARD_DELETE_READ),
        },
        {
            name: MENU_NAME_MODEL.GRAPHS,
            url: routes.graphs.list,
            label: 'graphs',
            onlyName: true,
            icon: IconsSideBar.icGraphs,
            isHovering: 30,
            hasPermission: permission?.includes(permissionObj.WEB_CHART_READ),
        },
        {
            name: MENU_NAME_MODEL.CARDS,
            url: routes.cards.list,
            label: 'card_management',
            onlyName: true,
            icon: IconsSideBar.icCardManagment,
            isHovering: 29,
            hasPermission: permission?.includes(permissionObj.WEB_CARD_READ),
        },
        {
            name: MENU_NAME_MODEL.DINING_MANAGEMENT,
            url: routes.foodie.list,
            label: 'dining_management',
            onlyName: true,
            icon: IconsSideBar.icDiningManagement,
            isHovering: 53,
            hasPermission: permission?.includes(permissionObj.WEB_FOODIE_SHIFT_READ) ||
                permission?.includes(permissionObj.WEB_FOODIE_CAFETERIA_READ) ||
                permission?.includes(permissionObj.WEB_FOODIE_MENU_READ) ||
                permission?.includes(permissionObj.WEB_FOODIE_ITEM_READ) ||
                permission?.includes(permissionObj.WEB_INGREDIENT_READ) ||
                permission?.includes(permissionObj.WEB_FOODIE_MEAL_TIME_READ) ||
                permission?.includes(permissionObj.WEB_FOODIE_ATTENDANCE_READ) ||
                permission?.includes(permissionObj.WEB_FOODIE_DEPARTMENT_POLICIES_READ) ||
                permission?.includes(permissionObj.WEB_TOTAL_MEALS_READ) ||
                permission?.includes(permissionObj.WEB_FOODIE_MEAL_FORECAST_READ) ||
                permission?.includes(permissionObj.WEB_FOODIE_ACCESS_READ) ||
                permission?.includes(permissionObj.WEB_FOODIE_REQUEST_READ) ||
                permission?.includes(permissionObj.WEB_FOODIE_REQUEST_HISTORY_READ) ||
                permission?.includes(permissionObj.WEB_FOODIE_MANAGE_SETTINGS_READ)
        },
        {
            name: MENU_NAME_MODEL.FOODIE_DINING,
            url: routes.foodieOrders.main,
            label: 'foodie_dining',
            onlyName: true,
            icon: IconsSideBar.icCafeteria,
            isHovering: 37,
            hasPermission: permission?.includes(permissionObj.WEB_FOODIE_MENU_SELECTION_VIEW) ||
                permission?.includes(permissionObj?.WEB_FOODIE_EXTRA_REQUEST_VIEW)
        },
        {
            name: MENU_NAME_MODEL.GUARD,
            url: routes.guard.list,
            label: 'guard',
            onlyName: true,
            icon: IconsSideBar.icGuard,
            isHovering: 27,
            hasPermission: isGuard,
        },
        {
            name: MENU_NAME_MODEL.PROFILE,
            url: routes.profile.list,
            label: 'profile',
            onlyName: true,
            icon: IconsSideBar.icProfile,
            isHovering: 21,
            // Residents always reach their own profile even without the privilege
            hasPermission: isResident || permission?.includes(permissionObj.WEB_PROFILE_READ),
        },
        {
            name: MENU_NAME_MODEL.SYSTEM_CONFIG,
            url: routes.systemConfig.list,
            label: 'system_config',
            onlyName: true,
            icon: IconsSideBar.icSystemSettings,
            isHovering: 31,
            hasPermission: permission?.includes(permissionObj.WEB_SYSTEM_CONFIG_READ),
        },
        {
            name: MENU_NAME_MODEL.DISTRIBUTION_CENTER,
            url: routes.distributionCenter.list,
            label: 'distribution_center',
            onlyName: true,
            icon: IconsSideBar.icDistributioCenter,
            isHovering: 33,
            hasPermission: permission?.includes(permissionObj.WEB_DISTRIBUTION_CENTER_READ),
        },
        {
            name: MENU_NAME_MODEL.APPROVAL,
            url: routes.approval.list,
            label: 'APPROVAL_FLOW',
            onlyName: true,
            icon: IconsSideBar.icApprovalFlow,
            isHovering: 21,
            hasPermission: permission?.includes(permissionObj.WEB_APPROVAL_FLOW_READ),
        },
        {
            name: MENU_NAME_MODEL.SERVICE_TYPE,
            url: routes.serviceType.list,
            label: 'service_type',
            onlyName: true,
            icon: IconsSideBar.icServiceType,
            isHovering: 34,
            hasPermission: permission?.includes(permissionObj.WEB_SERVICE_TYPE_READ)
        },
        {
            name: MENU_NAME_MODEL.TRANSPORT_REQUEST,
            url: routes.transportRequest.list,
            label: 'transport_request',
            onlyName: true,
            icon: IconsSideBar.icTransportRequest,
            isHovering: 35,
            hasPermission: permission?.includes(permissionObj.WEB_LOGISTIC_REQUEST_READ),
        },
        {
            name: MENU_NAME_MODEL.VISITOR_MANAGEMENT,
            url: isGuard ? routes.guard.visitor : routes.visitor.list,
            label: 'visitor_management',
            onlyName: true,
            icon: IconsSideBar.icVisitorManagment,
            isHovering: 36,
            hasPermission: isGuard ||
                    (permission?.includes(permissionObj.WEB_VISITOR_INVITATION_READ) ||
                    permission?.includes(permissionObj.WEB_VISITOR_REQUEST_READ) ||
                    permission?.includes(permissionObj.WEB_VISITOR_READ) ||
                    permission?.includes(permissionObj.WEB_VISITOR_TYPE_READ) ||
                    permission?.includes(permissionObj.WEB_REQUEST_ACCESS_READ))
        },
        {
            name: MENU_NAME_MODEL.BUILDINGS,
            url: routes.building.list,
            label: 'buildings',
            onlyName: true,
            icon: IconsSideBar.icBuildings,
            isHovering: 37,
            hasPermission: permission?.includes(permissionObj.WEB_BUILDING_READ)
        },
        {
            name: MENU_NAME_MODEL.MY_APARTMENTS,
            url: routes.myApartment.list,
            label: 'my_apartments',
            onlyName: true,
            icon: IconsSideBar.icMyApartments,
            isHovering: 37,
            // Residents always reach their own apartment even without the privilege
            hasPermission: isResident || permission?.includes(permissionObj.WEB_MY_BUILDING_READ)
        },
        {
            name: MENU_NAME_MODEL.INCOMING_INVITATIONS,
            url: routes.guard.incomingInvitation,
            label: 'incoming_invitations',
            onlyName: true,
            icon: IconsSideBar.icIncomingInvitation,
            isHovering: 38,
            hasPermission: isGuard,
        },
        {
            name: MENU_NAME_MODEL.TRAFFIC_MAIN_SYSTEM,
            url: routes.traffic.list,
            label: 'traffic_main_system',
            onlyName: true,
            icon: IconsSideBar.icTraffic,
            isHovering: 39,
            hasPermission: permission?.includes(permissionObj?.WEB_FINE_TYPE_READ) ||
                permission?.includes(permissionObj?.WEB_TRAFFIC_DASHBOARD_GEOFENCES_READ) ||
                permission?.includes(permissionObj?.WEB_TRAFFIC_DASHBOARD_FINES_READ) ||
                permission?.includes(permissionObj?.WEB_TRAFFIC_USERS_READ) ||
                permission?.includes(permissionObj?.WEB_TRAFFIC_MANAGE_FINES_READ) ||
                permission?.includes(permissionObj?.WEB_TRAFFIC_MANAGE_GEOFENCES_READ),
        },
        {
            name: MENU_NAME_MODEL.KITCHEN_TICKETS,
            url: routes.foodieKitchen.list,
            label: 'kitchen_tickets',
            onlyName: true,
            icon: IconsSideBar.icKitchenTickets,
            isHovering: 100,
            hasPermission: permission?.includes(permissionObj.WEB_KITCHEN_TICKETS_READ) || permission?.includes(permissionObj.WEB_KITCHEN_TICKETS_HISTORY_READ)
        },
        {
            name: MENU_NAME_MODEL.INCOMING_CONTRACTOR,
            url: routes.guard.incomingContractors,
            label: 'incoming_contractors',
            onlyName: true,
            icon: IconsSideBar.icIncomingContractor,
            isHovering: 40,
            hasPermission: isGuard,
        },
        {
            name: MENU_NAME_MODEL.DELIVERED_SERVICES,
            url: routes.deliveredServices.list,
            label: 'delivered_services',
            onlyName: true,
            icon: IconsSideBar.icDeliveredServices,
            isHovering: 101,
            hasPermission: permission?.includes(permissionObj.WEB_FOODIE_KITCHEN_ACCESS_READ) || permission?.includes(permissionObj.WEB_FOODIE_KITCHEN_ACCESS_HISTORY_READ)
        },
        {
            name: MENU_NAME_MODEL.ACCESS_MONITORING,
            url: routes.guard.access,
            label: 'access_monitoring',
            onlyName: true,
            icon: IconsSideBar.icAccessMonitoring,
            isHovering:41,
            hasPermission: isGuard,
        },
        {
            name: MENU_NAME_MODEL.EVENT_MONITORING,
            url: routes.eventMonitoring.list,
            label: 'event_monitoring',
            onlyName: true,
            icon: IconsSideBar.icEventMonitoring,
            isHovering: 141,
            // The guard opens the event monitoring whatever its permissions
            hasPermission: isGuard || permission?.includes(permissionObj.WEB_EVENT_MONITORING_READ),
        },
        {
            name: 'unregisteredExits',
            url: routes.unregisteredExits.list,
            label: 'unregistered_exits',
            onlyName: true,
            icon: IconsSideBar.IcUnregistered,
            isHovering:41,
            hasPermission: permission?.includes(permissionObj.WEB_UNREGISTERED_EXITS_READ),
        },
        // Material control is listed through three modules of its own, so the
        // menu is read by whoever reads at least one of them
        {
            name: 'materialControl',
            url: routes.materialControl.main,
            label: 'material_control',
            onlyName: true,
            icon: IconsSideBar.icMaterialRequest,
            isHovering: MODELS.ENTRY_AND_EXIT_FORMS,
            hasPermission: isGuard
                || permission?.includes(permissionObj.WEB_ENTRY_AND_EXIT_FORMS_READ)
                || permission?.includes(permissionObj.WEB_VEHICLE_MOVEMENTS_READ)
                || permission?.includes(permissionObj.WEB_MATERIAL_CONTROL_REQUEST_READ)
        },
    ];

    // Return the menu items ascending by the label shown in the active language
    return { menuItems: sortMenuByLabel(menuItems, (item) => t(item.label)) };
}

export default EmployeeTabsApp; // Export this component for use in other parts of the application
